import React, { useState } from 'react';
import { MapPin, Phone, Pencil, Star, Lock, RotateCcw, Settings, LayoutDashboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { openOffice } from '../../services/officeSelection';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { OfficeStats } from './OfficeStats';
import { OfficeForm } from './OfficeForm';

const addressLine = (o) => [o.address, o.city, o.state, o.zip].filter(Boolean).join(', ');

export const OfficeCard = ({ office: o, isAdmin, onEdit, onClose, onReopen, onMakeMain, practiceSide }) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const closed = o.isActive === false;
  return (
    <div className={`bg-white border rounded-2xl p-5 space-y-4 ${closed ? 'opacity-70 border-dashed' : 'border-slate-200'}`} data-testid={`office-card-${o.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-slate-900" data-testid={`office-name-${o.id}`}>{o.name}</h3>
            {o.isPrimary && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Main office</Badge>}
            {closed && <Badge variant="secondary" data-testid={`office-closed-badge-${o.id}`}>Closed</Badge>}
          </div>
          {addressLine(o) && <p className="text-sm text-slate-600 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" />{addressLine(o)}</p>}
          {o.phone && <p className="text-sm text-slate-600 flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{o.phone}</p>}
        </div>
        {isAdmin && !editing && (
          <div className="flex flex-wrap gap-2">
            {!closed && practiceSide && <Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => openOffice(o.id)} data-testid={`office-open-dashboard-${o.id}`}><LayoutDashboard className="w-4 h-4 mr-1" />Open dashboard</Button>}
            {!closed && practiceSide && <Button size="sm" variant="outline" onClick={() => navigate(`/offices/${o.id}/settings`)} data-testid={`office-settings-${o.id}`}><Settings className="w-4 h-4 mr-1" />Settings</Button>}
            {!closed && !practiceSide && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`office-edit-${o.id}`}><Pencil className="w-4 h-4 mr-1" />Edit</Button>}
            {!closed && !o.isPrimary && <Button size="sm" variant="outline" onClick={() => onMakeMain(o)} data-testid={`office-make-main-${o.id}`}><Star className="w-4 h-4 mr-1" />Make main</Button>}
            {!closed && !o.isPrimary && <Button size="sm" variant="outline" className="text-rose-700" onClick={() => onClose(o)} data-testid={`office-close-${o.id}`}><Lock className="w-4 h-4 mr-1" />Close office</Button>}
            {closed && <Button size="sm" variant="outline" onClick={() => onReopen(o)} data-testid={`office-reopen-${o.id}`}><RotateCcw className="w-4 h-4 mr-1" />Reopen</Button>}
          </div>
        )}
      </div>
      {editing
        ? <OfficeForm initial={o} testId={`office-edit-form-${o.id}`} onCancel={() => setEditing(false)} onSave={async (f) => { if (await onEdit(o, f)) setEditing(false); }} />
        : <OfficeStats stats={o.stats} testId={`office-stats-${o.id}`} />}
    </div>
  );
};
